import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UIFeedbackProvider } from './contexts/UIFeedbackContext';
import { SavedRecipesProvider } from './contexts/SavedRecipesContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import CookieBanner from './components/CookieBanner';
import { track } from './utils/analytics';
import { isFirebaseReachable } from './utils/connectivity';

// Route-level code splitting: each page loads on demand, so the initial
// bundle no longer parses every screen up front.
// One screen for both /login and /signup -- see the note in AuthPage.js.
const AuthPage = lazy(() => import('./pages/AuthPage'));
const BrowsePage = lazy(() => import('./pages/BrowsePage'));
const CreateRecipePage = lazy(() => import('./pages/CreateRecipePage'));
const RecipeDetailPage = lazy(() => import('./pages/RecipeDetailPage'));
const GenerateRecipePage = lazy(() => import('./pages/GenerateRecipePage'));
const MyRecipesPage = lazy(() => import('./pages/MyRecipesPage'));
const MealPlanPage = lazy(() => import('./pages/MealPlanPage'));
const FollowingPage = lazy(() => import('./pages/FollowingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const EditRecipePage = lazy(() => import('./pages/EditRecipePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

function PageFallback() {
  return (
    <div className="main-content" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}>
      <div className="ai-loading-dots"><span></span><span></span><span></span></div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
}

// Shown instead of the full Navbar for signed-out visitors on a public
// profile/recipe -- without this they'd land on real content with zero way
// to get to Sign in/Join, which defeats the point of a public, shareable
// page (the whole "no growth loop" gap this feature exists to close).
function GuestBar() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px var(--space-md)', borderBottom: '1px solid var(--border-light)'
    }}>
      <Link to="/login" className="navbar-brand">
        <div className="navbar-logo">tastr<span>.</span></div>
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        {/* Someone who arrived from a shared link is deciding whether this
            thing is worth an account. Where it's going is part of that. */}
        <a href="/roadmap" className="btn btn-ghost btn-sm roadmap-guest">Roadmap</a>
        <Link to="/login" className="btn btn-ghost btn-sm">Sign in</Link>
        <Link to="/signup" className="btn btn-primary btn-sm">Join Tastr</Link>
      </div>
    </div>
  );
}

// Says the quiet part out loud when Firebase is unreachable.
//
// Without this the app fails INVISIBLY: hosting serves fine from tastr.app,
// Firestore returns nothing, and every page renders its empty state -- so a
// VPN blocking googleapis.com looks identical to an app with no content.
// That is what "the app doesn't work" turned out to mean, and there was
// nothing on screen to suggest a network problem rather than a broken build.
//
// Checked once per mount, not polled: this is a configuration condition, not
// a flaky one, and a banner that flickers is worse than no banner. Failing
// open is deliberate -- if the probe itself is inconclusive we say nothing
// rather than accusing a working network.
function ConnectivityBanner() {
  const [blocked, setBlocked] = React.useState(false);

  useEffect(() => {
    let active = true;
    isFirebaseReachable().then((ok) => {
      if (!active || ok) return;
      setBlocked(true);
      // No URL, no uid, no user content -- just the fact that it happened.
      track('firebase_unreachable');
    });
    return () => { active = false; };
  }, []);

  if (!blocked) return null;

  return (
    <div style={{
      background: 'var(--tomato)',
      color: 'white',
      textAlign: 'center',
      padding: '8px var(--space-md)',
      fontSize: '0.85rem'
    }}>
      Tastr can't reach its database. A VPN, firewall or ad blocker is most
      likely blocking <code>googleapis.com</code> — recipes won't load until
      it's allowed through.
    </div>
  );
}

function VerifyEmailBanner() {
  const { needsEmailVerification, resendVerificationEmail, refreshUser } = useAuth();
  const [sent, setSent] = React.useState(false);

  if (!needsEmailVerification) return null;

  const linkStyle = {
    background: 'none',
    border: 'none',
    color: 'white',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit'
  };

  return (
    <div style={{
      background: 'var(--turmeric)',
      color: 'white',
      textAlign: 'center',
      padding: '8px var(--space-md)',
      fontSize: '0.85rem'
    }}>
      Verify your email to publish, rate, or use the AI Chef.{' '}
      <button
        onClick={() => resendVerificationEmail().then(() => setSent(true)).catch(() => {})}
        disabled={sent}
        style={linkStyle}
      >
        {sent ? 'Sent!' : 'Resend email'}
      </button>
      {' · '}
      <button onClick={refreshUser} style={linkStyle}>
        I've verified
      </button>
    </div>
  );
}

// GA4's "enhanced measurement" can pick up history changes on its own, but
// it is unreliable with client-side routers: it fires on pushState before
// React has rendered the new screen, so titles and paths can disagree. This
// sends one page_view per route change, deliberately and after render.
//
// IMPORTANT: this DOUBLE COUNTS unless "Page changes based on browser
// history events" is turned OFF in GA4 > Admin > Data streams > Enhanced
// measurement. Explicit tracking and enhanced measurement must not both be
// on. Note that page_path here is the ROUTE, not the resolved id, so a
// recipe page reports /recipe/:id rather than leaking which recipe -- the
// recipe_id already travels on recipe_view where it belongs.
function usePageViews() {
  const location = useLocation();
  useEffect(() => {
    track('page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title
    });
  }, [location.pathname]);
}

function AppRoutes() {
  const { currentUser } = useAuth();
  const location = useLocation();
  usePageViews();
  // Browse ('/') is public now too, so signed-out visitors landing from a
  // shared link have somewhere to go rather than hitting a login wall.
  const onPublicContent = location.pathname === '/'
    || location.pathname.startsWith('/user/')
    || location.pathname.startsWith('/recipe/');

  return (
    <div className="app-container">
      <ConnectivityBanner />
      <VerifyEmailBanner />
      {currentUser ? <Navbar /> : onPublicContent && <GuestBar />}
      {/* Inner boundary: a crash in one page keeps the navbar, so the user
          can navigate away instead of being stranded. Keyed on the route so
          moving to a different page clears the error -- without the key, one
          bad recipe would poison every subsequent screen. */}
      <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/login" element={
          currentUser ? <Navigate to="/" /> : <AuthPage mode="signin" />
        } />
        <Route path="/signup" element={
          currentUser ? <Navigate to="/" /> : <AuthPage mode="signup" />
        } />
        <Route path="/" element={
          /* Not wrapped in ProtectedRoute: the feed is open to anonymous
             visitors, and the recipes read rule (publiclyVisible) is what
             limits what they can actually see. */
          <BrowsePage />
        } />
        <Route path="/create" element={
          <ProtectedRoute><CreateRecipePage /></ProtectedRoute>
        } />
        {/* Not wrapped in ProtectedRoute: a recipe whose author has opted
            into public visibility must be reachable by anonymous visitors
            (shareable links, no login wall). Firestore rules are the real
            gate -- a private recipe simply won't load for a signed-out
            visitor, same as any other permission failure. */}
        <Route path="/recipe/:id" element={<RecipeDetailPage />} />
        <Route path="/recipe/:id/edit" element={
           <ProtectedRoute><EditRecipePage /></ProtectedRoute>
        } />
        <Route path="/generate" element={
          <ProtectedRoute><GenerateRecipePage /></ProtectedRoute>
        } />
        <Route path="/my-recipes" element={
          <ProtectedRoute><MyRecipesPage /></ProtectedRoute>
        } />
        <Route path="/plan" element={
          <ProtectedRoute><MealPlanPage /></ProtectedRoute>
        } />
        <Route path="/following" element={
          <ProtectedRoute><FollowingPage /></ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />
        {/* Same reasoning as /recipe/:id above -- public profiles need to be
            reachable without signing in. */}
        <Route path="/user/:uid" element={<PublicProfilePage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      {(currentUser || onPublicContent) && (
              <footer style={{
                textAlign: 'center',
                padding: 'var(--space-xl) var(--space-md)',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                letterSpacing: '0.03em'
              }}>
                <div>Developed by Nate Anderson in Colorado</div>
                {/* Plain <a>, not <Link>: /roadmap is a static page under
                    public/, not a React route, so the router must not try to
                    handle it. */}
                <a href="/roadmap" style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'underline',
                  marginTop: '4px',
                  display: 'inline-block'
                }}>Roadmap</a>
                <span style={{ margin: '0 6px' }}>·</span>
                <a href="/privacy" style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'underline',
                  marginTop: '4px',
                  display: 'inline-block'
                }}>Privacy Policy</a>
                <span style={{ margin: '0 6px' }}>·</span>
                <a href="/terms" style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'underline',
                  marginTop: '4px',
                  display: 'inline-block'
                }}>Terms of Service</a>
                <span style={{ margin: '0 6px' }}>·</span>
                <a
                  href="mailto:nate@tastr.app?subject=Tastr%20feedback%20(beta)&body=What%20I%20liked%2C%20what%20broke%2C%20what%27s%20missing%3A%0A%0A"
                  style={{
                    color: 'var(--green-600)',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    marginTop: '4px',
                    display: 'inline-block'
                  }}
                >Send feedback</a>
              </footer>
            )}
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    // Outer boundary catches what the inner one can't -- a failure in the
    // Navbar, the auth provider, or the router itself. Its fallback uses a
    // plain <a>, so it still works with the router unmounted.
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <UIFeedbackProvider>
            {/* Inside AuthProvider: the saved set is per-user and reloads
                when the signed-in user changes. */}
            <SavedRecipesProvider>
              <AppRoutes />
            </SavedRecipesProvider>
          </UIFeedbackProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
